import { CircleCheck, CircleDashed, ExternalLink, GitBranch, MoreHorizontal, OctagonX, PanelRight } from 'lucide-react';
import { IncidenteSelo } from '@/components/patterns/incidente-badges';
import { NoPainelBadge, StatusBuildBadge, StatusIcone } from '@/components/patterns/status-badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { BuildResumo, StatusBuild } from '@/data/types';
import { usePodeVer } from '@/hooks/use-acesso';
import { DownloadLinks } from '@/features/versao/download-links';
import { ROTULO_STATUS, type AcaoStatus } from '@/lib/capabilities';
import { formatarDataHora, formatarHora, formatarRelativo } from '@/lib/format';
import { cn } from '@/lib/utils';
import { OQueMudou } from './MudancasInline';

/**
 * Uma publicação do feed — o "post" da timeline.
 *
 *  ┌──────────────────────────────────────────────────────┐
 *  │ Jenkins publicou Instalador #78 · [v2.2602] · há 2h ⋯│  autoria
 *  │ 2.2602.78.0  [Não homologada] [No painel] [2 incid.] │  versão em destaque
 *  │ ✔ Homologada por fulano · há 2h                      │  atividade
 *  │ ┌ O QUE MUDOU · 46 artefatos      [Ver mudanças ▾] ┐ │  corpo
 *  │ [Instalador] [Instalador cliente]        [Detalhes]  │  rodapé
 *  └──────────────────────────────────────────────────────┘
 *
 * O ponto na linha vertical da timeline (fora do card) leva a cor do status.
 */

const COR_PONTO: Record<StatusBuild, string> = {
  homologado: 'border-success bg-success',
  reprovado: 'border-danger bg-danger',
  nao_homologado: 'border-border-strong bg-bg-page',
};

const VERBO: Record<StatusBuild, string> = {
  homologado: 'Homologada',
  reprovado: 'Reprovada',
  nao_homologado: 'Devolvida para não homologada',
};

export function PublicacaoCard({
  build,
  ativo,
  acoesRapidas,
  aoAbrir,
  aoExecutarAcao,
  emAcao,
}: {
  build: BuildResumo;
  ativo?: boolean;
  /** Ações de status já resolvidas para as roles da sessão. */
  acoesRapidas: AcaoStatus[];
  aoAbrir: (build: BuildResumo) => void;
  aoExecutarAcao: (build: BuildResumo, acao: AcaoStatus) => void;
  emAcao?: boolean;
}) {
  const avaliada = build.status_por || build.status_em;
  return (
    <li className="relative pb-4 pl-7 sm:pl-9">
      <span
        aria-hidden="true"
        className={cn(
          'absolute left-0 top-[22px] size-[15px] rounded-full border-2 ring-4 ring-bg-page',
          COR_PONTO[build.status],
        )}
      />
      <article
        aria-label={`Versão ${build.versao}`}
        className={cn(
          'flex flex-col gap-3 rounded-card border bg-bg-surface p-4 shadow-sm transition-colors duration-fast ease-standard',
          ativo ? 'border-primary-border bg-primary-wash' : 'border-border hover:border-border-strong',
          build.no_painel && !ativo && 'border-primary-border',
        )}
      >
        {/* Autoria */}
        <div className="flex items-start gap-2">
          <p className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 text-cell text-on-surface-muted">
            <span className="font-bold text-on-surface">{build.jenkins ? 'Jenkins publicou' : 'Versão publicada'}</span>
            {build.jenkins?.job ? (
              build.jenkins.url ? (
                <a
                  href={build.jenkins.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 font-mono text-primary underline-offset-4 hover:underline"
                >
                  {build.jenkins.job}
                  {build.jenkins.build != null ? ` #${build.jenkins.build}` : ''}
                  <ExternalLink className="size-3" aria-hidden="true" />
                  <span className="sr-only">(abre o build no Jenkins em nova aba)</span>
                </a>
              ) : (
                <span className="font-mono">
                  {build.jenkins.job}
                  {build.jenkins.build != null ? ` #${build.jenkins.build}` : ''}
                </span>
              )
            ) : null}
            <span className="inline-flex items-center gap-1 rounded-chip border border-border bg-bg-subtle px-1.5 py-px font-mono text-label font-normal text-on-surface">
              <GitBranch className="size-3 text-icon" aria-hidden="true" />
              {build.branch}
            </span>
            <time dateTime={build.publicado_em} title={formatarDataHora(build.publicado_em)}>
              {formatarRelativo(build.publicado_em)} · {formatarHora(build.publicado_em)}
            </time>
          </p>
          <MenuAcoes build={build} acoes={acoesRapidas} aoAbrir={aoAbrir} aoExecutar={aoExecutarAcao} ocupado={emAcao} />
        </div>

        {/* Versão em destaque */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <button
            type="button"
            onClick={() => aoAbrir(build)}
            aria-label={`Abrir o detalhe da versão ${build.versao}`}
            className="rounded-button text-left font-mono text-section-title tabular-nums text-on-surface underline-offset-4 transition-colors duration-fast ease-standard hover:text-primary hover:underline"
          >
            {build.versao}
          </button>
          <StatusBuildBadge status={build.status} />
          {build.no_painel ? <NoPainelBadge /> : null}
          <IncidenteSelo
            quantidade={build.incidentes_abertos}
            severidade={build.incidente_severidade_max}
            versao={build.versao}
          />
        </div>

        {/* Atividade */}
        {avaliada ? (
          <div className="flex flex-col gap-1">
            <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-cell text-on-surface-muted">
              <span
                className={cn(
                  'inline-flex items-center gap-1 font-bold [&_svg]:size-3.5',
                  build.status === 'homologado' && 'text-success',
                  build.status === 'reprovado' && 'text-danger',
                )}
              >
                {build.status === 'nao_homologado' ? <CircleDashed aria-hidden="true" /> : <StatusIcone status={build.status} />}
                {VERBO[build.status]}
              </span>
              {build.status_por ? (
                <span>
                  por <span className="break-all text-on-surface">{build.status_por}</span>
                </span>
              ) : null}
              {build.status_em ? (
                <>
                  <span aria-hidden="true">·</span>
                  <time dateTime={build.status_em} title={formatarDataHora(build.status_em)}>
                    {formatarRelativo(build.status_em)}
                  </time>
                </>
              ) : null}
            </p>
            {build.status_motivo && build.status !== 'homologado' ? (
              <p
                className={cn(
                  'rounded-input border-l-[3px] px-3 py-1.5 text-cell text-on-surface',
                  build.status === 'reprovado' ? 'border-l-danger bg-danger-wash' : 'border-l-border-strong bg-bg-subtle',
                )}
              >
                <span className="font-bold">Motivo:</span> {build.status_motivo}
              </p>
            ) : null}
          </div>
        ) : null}

        <OQueMudou build={build} />

        {/* Rodapé */}
        <div className="flex flex-wrap items-center gap-2 border-t border-border-subtle pt-3">
          <DownloadLinks links={build.links} versao={build.versao} />
          <Button variant="ghost" size="sm" className="ml-auto" onClick={() => aoAbrir(build)}>
            <PanelRight aria-hidden="true" />
            Detalhes
            <span className="sr-only"> da versão {build.versao}</span>
          </Button>
        </div>
      </article>
    </li>
  );
}

const ICONE_ACAO: Record<StatusBuild, typeof CircleCheck> = {
  homologado: CircleCheck,
  reprovado: OctagonX,
  nao_homologado: CircleDashed,
};

export function MenuAcoes({
  build,
  acoes,
  aoAbrir,
  aoExecutar,
  ocupado,
}: {
  build: BuildResumo;
  acoes: AcaoStatus[];
  aoAbrir: (build: BuildResumo) => void;
  aoExecutar: (build: BuildResumo, acao: AcaoStatus) => void;
  ocupado?: boolean;
}) {
  const verIncidentes = usePodeVer('incidentes');
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="-mr-1.5 -mt-1 size-8 shrink-0 text-icon"
          aria-label={`Mais ações da versão ${build.versao}`}
          loading={ocupado}
        >
          <MoreHorizontal aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-72">
        <DropdownMenuLabel>Homologação</DropdownMenuLabel>
        {acoes.map((acao) => {
          const Icone = ICONE_ACAO[acao.destino];
          const bloqueada = !acao.bloqueio.permitido;
          return (
            <DropdownMenuItem
              key={acao.destino}
              disabled={bloqueada}
              onSelect={() => aoExecutar(build, acao)}
              className="items-start"
            >
              <Icone
                className={cn(
                  'mt-0.5 size-4 shrink-0',
                  !bloqueada && acao.destino === 'homologado' && 'text-success',
                  !bloqueada && acao.destino === 'reprovado' && 'text-danger',
                )}
                aria-hidden="true"
              />
              <span className="flex min-w-0 flex-col">
                <span>
                  {acao.rotulo}
                  <span className="sr-only"> {ROTULO_STATUS[acao.destino]}</span>
                </span>
                {bloqueada ? (
                  <span className="text-label font-normal text-on-surface-muted">{acao.bloqueio.motivo}</span>
                ) : acao.destino === 'homologado' && build.incidentes_abertos > 0 && verIncidentes ? (
                  <span className="text-label font-normal text-warning-text">
                    Há incidente em aberto: pede confirmação.
                  </span>
                ) : null}
              </span>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => aoAbrir(build)}>
          <PanelRight className="size-4 shrink-0" aria-hidden="true" />
          Abrir detalhes
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
